import { isExternalHref, isInternalHref } from '@/components/link/utils';

export type PreparePrivacyHtmlOptions = {
    linkClassName?: string;
    prefixHref?: (href: string) => string;
    /** When set, external new-tab links get aria-describedby pointing at this id. */
    openInNewTabDescribedById?: string;
};

const ANCHOR_OPEN_TAG_RE = /<a\s+([^>]*?)>/gi;

/**
 * Prepares CMS-authored privacy consent HTML for `dangerouslySetInnerHTML`:
 * prefixes internal links, injects link classes, and hardens external anchors.
 */
export function preparePrivacyConsentHtml(html: string, options: PreparePrivacyHtmlOptions = {}): string {
    const { linkClassName, prefixHref, openInNewTabDescribedById } = options;

    return html.replace(ANCHOR_OPEN_TAG_RE, (_full, attrs: string) => {
        let newAttrs = attrs;

        if (prefixHref) {
            newAttrs = newAttrs.replace(/href=(["'])([^"']+)\1/i, (match, quote: string, href: string) => {
                const trimmedHref = href.trim();
                if (isInternalHref(trimmedHref)) {
                    return `href=${quote}${prefixHref(trimmedHref)}${quote}`;
                }
                return match;
            });
        }

        if (linkClassName && !/\bclass\s*=/i.test(newAttrs)) {
            newAttrs = `class="${linkClassName}" ${newAttrs}`;
        }

        const hrefMatch = newAttrs.match(/href=(["'])([^"']+)\1/i);
        const href = hrefMatch?.[2]?.trim();
        if (href && isExternalHref(href)) {
            if (!/\btarget\s*=/i.test(newAttrs)) {
                newAttrs += ' target="_blank"';
            }
            if (!/\brel\s*=/i.test(newAttrs)) {
                newAttrs += ' rel="noopener noreferrer"';
            }
        }

        // Every link that opens a new tab (internal ones included) announces it as its description.
        if (
            openInNewTabDescribedById &&
            /\btarget\s*=\s*["']?_blank/i.test(newAttrs) &&
            !/\baria-describedby\s*=/i.test(newAttrs)
        ) {
            newAttrs += ` aria-describedby="${openInNewTabDescribedById}"`;
        }

        return `<a ${newAttrs.trim()}>`;
    });
}

const TRAILING_LINK_RE = /^([\s\S]*?)\s*(<a\b[^>]*>(?:(?!<a\b)[\s\S])*?<\/a>)\s*$/i;

/**
 * Splits a trailing "disclosure" link (e.g. "... <a>Espandi</a>") from the consent text, so the link can be
 * rendered next to, not inside, the checkbox label: a link inside the label becomes part of the control's name.
 * Returns the whole html as text when it does not end with a link or the link would be the only content.
 */
export function splitTrailingLink(html: string): { text: string; link: string | null } {
    const match = html.match(TRAILING_LINK_RE);
    if (!match || match[1].replace(/<[^>]*>/g, '').trim() === '') {
        return { text: html, link: null };
    }
    return { text: match[1], link: match[2] };
}
